import AtlasSDK from '@olxeu-a2b/core/src/api/AtlasSDK';
import ChatSDK from '@olxeu-a2b/core/src/api/ChatSDK';
import DeliverySDK from '@olxeu-a2b/core/src/api/DeliverySDK';
import { APIMiddleware, HTTP_STATUS } from '@olxeu-a2b/core/typings/APIRequester';
import cookie from '@olxeu-a2b/delivery-utils/src/extras/cookie';
import { getConfig } from './getConfig';
import promiseRetry from './promiseRetry';
import redirectToLogin from './redirectToLogin';
import { getViewPort } from './viewPortObserver';

const { server } = getConfig('deliverySDK');
const { server: atlasServer, version } = getConfig('atlasSDK');
const { server: chatServer } = getConfig('chatSDK');
const api = getConfig('api');
const env = getConfig('environment');

const developmentMiddleware: APIMiddleware = response => {
  if (
    process.env.NODE_ENV === 'development' &&
    response.status === (401 as HTTP_STATUS.UNAUTHORIZED)
  ) {
    cookie.remove('access_token');
  }
  return response;
};

const redirectMiddleware: APIMiddleware = response => {
  if (
    process.env.NODE_ENV === 'production' &&
    response.status === (401 as HTTP_STATUS.UNAUTHORIZED)
  ) {
    redirectToLogin(getViewPort().desktop);
  }
  return response;
};

export const errorMiddleware: APIMiddleware = (response, originalCaller) => {
  if (response.error) {
    if (
      response.status === (503 as HTTP_STATUS.SERVICE_UNAVAILABLE) &&
      response.headers?.get('Retry-After')
    ) {
      const delay: number = parseInt(response.headers.get('Retry-After'), 10);
      if (delay <= 2) {
        return promiseRetry(originalCaller, 3, delay);
      }
      throw new Error(`Service unavailable, try again on ${delay / 60} minutes`);
    }
    throw response;
  }
  return response.body;
};

let sdk: DeliverySDK = null;
export const sdkFactory = () => {
  if (!sdk) {
    sdk = new DeliverySDK(
      server,
      {
        token: cookie.get('access_token'),
      },
      {
        customBasePath: '',
        customUrl: env === 'production' ? api.production : api.staging,
      },
    );
    sdk.appendMiddleware(redirectMiddleware);
    sdk.appendMiddleware(errorMiddleware);
  }

  return sdk;
};

let atlasSDK: AtlasSDK = null;

export const clearAtlasSDKInstance = (): void => {
  atlasSDK = null;
};

export const atlasSDKFactory = (apiVersion?: string) => {
  if (!atlasSDK) {
    atlasSDK = new AtlasSDK(atlasServer, apiVersion || version, {
      token: cookie.get('access_token'),
    });

    /*
     * Don't remove this check, as it will make sure to drop this
     * whole code block on production builds
     */
    if (process.env.NODE_ENV === 'development') {
      atlasSDK.appendMiddleware(developmentMiddleware);
    }

    atlasSDK.appendMiddleware(redirectMiddleware);
    atlasSDK.appendMiddleware(errorMiddleware);
  }

  return atlasSDK;
};

let chatSDK: ChatSDK = null;
export const chatSDKFactory = (): ChatSDK => {
  if (!chatSDK) {
    chatSDK = new ChatSDK(chatServer, {
      token: cookie.get('access_token'),
    });

    /*
     * Don't remove this check, as it will make sure to drop this
     * whole code block on production builds
     */
    if (process.env.NODE_ENV === 'development') {
      chatSDK.appendMiddleware(developmentMiddleware);
    }
  }

  return chatSDK;
};
